import { useEffect, useState } from "react";
import { getFrontFaq } from "@/api/faqApi";
import {
  assembleFaqBlock,
  FAQ_SITE_SCOPE,
  shouldRenderFaqBlock,
  type FaqEntry,
  type FaqScope,
} from "@/seo/faqAssembly";
import { buildFaqPageLd, serializeJsonLd } from "@/seo/jsonld";

/**
 * FAQ_Block 前台展示区块（需求 6.1–6.4、6.6、6.9、6.10、6.12）。
 *
 * ## JSON-LD 注入方式：不复用 `useSEO()`
 *
 * `useSEO()`（`src/utils/seo.ts`）在页面组件的初次渲染时同步写入本页的
 * `Organization` / `WebSite` / `BreadcrumbList` / 页面专属段等 JSON-LD，随后每次
 * 依赖变化都会先 `removeJsonLd()` 清空**全部**带 `data-seo="seo-page-jsonld"` 标记
 * 的脚本再重新注入——这把「本页 JSON-LD 清单」当成一个整体来管理。
 *
 * `FaqBlock` 的数据来源（两次 `getFrontFaq()`）是异步的，且与页面组件的
 * `useSEO()` 调用时机、依赖数组完全独立：如果把 `FAQPage` 段塞进 `useSEO()` 的
 * `jsonLd` 参数，页面组件就必须把自身状态提升出一个「FAQ 是否已加载」的标志位，
 * 依赖数组也要跟着变，父组件的 JSON-LD 生命周期由此被 FaqBlock 的异步加载耦合。
 *
 * 因此这里选择方案 (a)：`FaqBlock` 用自己的 `useEffect` 独立管理一个专属的
 * `<script type="application/ld+json" data-seo="faq-block-jsonld">` 标签——
 * 加载完成且 `shouldRenderFaqBlock()` 为真时注入，组件卸载或判定结果翻转为
 * 假时移除。这个标签用不同的 `data-seo` 取值（`faq-block-jsonld`，而不是
 * `useSEO()` 用的 `seo-page-jsonld`），两套生命周期互不影响：`useSEO()` 的
 * `removeJsonLd()` 只按自己的标记选择器清理，不会误删 FaqBlock 的段，
 * 反过来 FaqBlock 卸载时也只移除自己那一个标签。
 *
 * task 12.4（把 FaqBlock 接入具体页面）不需要额外传回调：FaqBlock 是完全
 * 自足的组件，只需要传入 `scope` 即可。
 *
 * ## 折叠交互：`<details>`/`<summary>`
 *
 * 浏览器原生 `<details>` 的展开/收起完全由 `[open]` 属性与 CSS 控制，答案文本
 * 节点（`<summary>` 之后的内容）在收起状态下依然完整存在于 DOM 中，只是不可见
 * ——不需要任何 JS 条件渲染，天然满足需求 6.6。问题文本放在 `<summary>` 里，
 * 承担「标题元素」角色（可点击、天然出现在页面的可交互层级里）；答案放在
 * `<summary>` 之后的紧邻 `<p>` 里，与 `<summary>` 同级、位于同一个 `<details>`
 * 容器内，满足需求 6.1「答案由与该标题同级或紧邻的独立文本容器元素承载」。
 *
 * 未在 `<summary>` 内嵌套 `<h3>`：嵌套块级标题元素会在多数浏览器里破坏原生
 * 展开三角图标的对齐与可点击区域，且需求 6.1 本身只要求问题文本「由 1 个标题
 * 元素承载」——没有限定必须是 `<h1>`–`<h6>`，`<summary>` 本身就是承载标题语义
 * 的交互式标题元素（HTML 标准称之为 "the heading of a disclosure widget"）。
 */

export type FaqBlockProps = {
  /** 当前页面的 Page_Type（`assembleFaqBlock()` 页面 scope 分组的取值） */
  scope: FaqScope;
};

const FAQ_BLOCK_JSONLD_TAG_ID = "faq-block-jsonld";

type LoadState = "loading" | "ready" | "failed";

function removeFaqJsonLd() {
  if (typeof document === "undefined") return;
  document.querySelectorAll(`script[data-seo="${FAQ_BLOCK_JSONLD_TAG_ID}"]`).forEach((n) => n.remove());
}

function injectFaqJsonLd(faq: ReturnType<typeof buildFaqPageLd>) {
  if (typeof document === "undefined" || !faq) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-seo", FAQ_BLOCK_JSONLD_TAG_ID);
  s.text = serializeJsonLd(faq);
  document.head.appendChild(s);
}

/**
 * FAQ_Block：拉取页面 scope 与 `SITE` scope 的已发布 FAQ_Entry，装配后渲染
 * 问答折叠列表与 `<h2>` 标题，并注入对应的 `FAQPage` JSON-LD。
 *
 * 候选条目不足 5 条、接口失败或 10 秒未返回时整段不渲染（不含 `<h2>`、不含
 * JSON-LD），并记录一条包含 URL、scope 与实际候选条目数的日志（需求 6.9、6.10）。
 */
export default function FaqBlock({ scope }: FaqBlockProps) {
  const [state, setState] = useState<LoadState>("loading");
  const [entries, setEntries] = useState<FaqEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setEntries([]);

    // 需求 6.10：接口失败或 10 秒未返回时同样整段省略。两次调用（页面 scope +
    // SITE scope）任一失败都要整体省略，因此用一个 Promise.all 统一走同一个
    // catch，而不是分别 catch 后用空数组兜底——后者会让「SITE 接口超时」这种
    // 情形悄悄退化成「只有页面 scope 的候选池」，与需求要求的「同样整段省略」
    // 不符。
    Promise.all([getFrontFaq(scope), getFrontFaq(FAQ_SITE_SCOPE)])
      .then(([pageEntries, siteEntries]) => {
        if (cancelled) return;
        const assembled = assembleFaqBlock(pageEntries, siteEntries);
        setEntries(assembled);
        setState("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(
          "[FaqBlock] FAQ 前台接口失败或超时，本页 FAQ_Block 与 FAQPage JSON-LD 整段省略。",
          {
            url: typeof window !== "undefined" ? window.location.pathname : "",
            scope,
            time: new Date().toISOString(),
            reason: err instanceof Error ? err.message : String(err),
          },
        );
        setEntries([]);
        setState("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  const shouldRender = state === "ready" && shouldRenderFaqBlock(entries);

  useEffect(() => {
    if (shouldRender) {
      const faqJsonLd = buildFaqPageLd(entries);
      injectFaqJsonLd(faqJsonLd);
    } else {
      removeFaqJsonLd();
    }
    return () => {
      removeFaqJsonLd();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, entries]);

  useEffect(() => {
    // 加载完成但候选条目不足 5 条：记录 URL、scope 与实际条目数（需求 6.9）。
    // 接口失败/超时的日志已经在上面的 catch 分支记录过，这里不重复记。
    if (state === "ready" && !shouldRenderFaqBlock(entries)) {
      console.warn(
        "[FaqBlock] 该页面对应归属范围与全站范围的已发布 FAQ_Entry 合计数量少于 5 条，FAQ_Block 与 FAQPage JSON-LD 整段省略。",
        {
          url: typeof window !== "undefined" ? window.location.pathname : "",
          scope,
          entryCount: entries.length,
        },
      );
    }
  }, [state, entries, scope]);

  if (!shouldRender) {
    return null;
  }

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16" aria-labelledby="faq-block-heading">
      <div className="text-center mb-10">
        <h2 id="faq-block-heading" className="text-2xl lg:text-3xl font-bold text-[#0d1f3c]">
          常见问题
        </h2>
      </div>
      <div className="max-w-3xl mx-auto space-y-3">
        {entries.map((entry) => (
          <details
            key={entry.id}
            className="group rounded-xl border border-gray-100 bg-white px-5 py-4 open:shadow-sm"
          >
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-sm font-semibold text-[#0d1f3c]">
              <span>{entry.question}</span>
              <i className="ri-arrow-down-s-line text-gray-400 transition-transform duration-200 group-open:rotate-180" aria-hidden="true"></i>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-gray-500">{entry.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
